import { useMemo } from "react";
import Scrollbars from "react-custom-scrollbars-2";
import { Swiper, SwiperSlide } from "swiper/react";
import styled from "styled-components";
import { Autoplay, EffectFade, FreeMode, Navigation, Pagination } from "swiper/modules";
import { useFormikContext } from "formik";
import { Button, CardMedia, Divider, Stack, TextField, Typography } from "@mui/material";
import QuantitySelector from "../../../components/quantity-selector/QuantitySelector";
import ProductSettings from "./ProductSettings";
import { FormValues } from "../ProductDetails";
import parse from "html-react-parser";
import { Setting, Content } from "@/types";
import Price from "@/components/Price";
import { multiplyByQuantity } from "@/utils/numbers";
import RelatedProducts from "./RelatedProducts";
import ScheduleAvailability from "@/components/ScheduleAvailability";
import { isAvailableToday } from "@/utils/weekDays";
import { useAppSelector } from "@/hooks";
import { getThumbnailImage } from "@/utils/media";
import { IMAGE_PLACEHOLDER } from "@/constants/global";

const SwiperStyled = styled(Swiper)`
  .swiper-pagination {
    bottom: 46px;
  }
`;

interface Props {
  dish: Content;
  settings: Setting[];
  isLoading: boolean;
}

const RestaurantForm = ({ dish, settings, isLoading }: Props) => {
  const organizationId = useAppSelector((store) => store.ui?.activeOrganization);
  const activeBranch = useAppSelector((store) => store.ui.activeBranch);
  const { values, handleChange, handleSubmit, setFieldValue } = useFormikContext<FormValues>();

  const price = useMemo(() => {
    let dishPrice = Number(dish?.price ?? 0);
    Object.entries(values?.settings).forEach(([settingId, optionIds]) => {
      const setting = settings.find((s) => s.id === settingId);
      const newOptions = setting?.options?.filter((o) => optionIds.includes(o.id));
      const optionPrices = (newOptions ?? []).map((o) => Number(o.price ?? 0));
      const totalOptionPrice = optionPrices.reduce((total, price) => total + price, 0);
      dishPrice = dishPrice + totalOptionPrice;
    });
    return dishPrice;
  }, [dish?.price, settings, values?.settings]);

  const increaseQuantity = () => {
    const quantity = values.quantity + 1;
    setFieldValue("quantity", quantity);
  };

  const reduceQuantity = () => {
    if (values.quantity === 1) return;
    const quantity = values.quantity - 1;
    setFieldValue("quantity", quantity);
  };

  const isDishAvailableToday = useMemo(() => {
    if (activeBranch) {
      return isAvailableToday(dish?.branchSettings?.[activeBranch]?.schedule);
    }
    return isAvailableToday(dish?.schedule);
  }, [dish?.schedule, dish?.branchSettings, activeBranch]);

  const isDisabled = useMemo(() => {
    return Boolean(dish?.isDisabled) || Boolean(activeBranch && dish?.branchSettings?.[activeBranch]?.isDisabled);
  }, [dish?.isDisabled, dish?.branchSettings, activeBranch]);

  return (
    <Stack
      flex={1}
      sx={{
        boxSizing: "border-box",
      }}
    >
      <Scrollbars autoHide style={{ flex: 1 }}>
        {dish?.images?.length ? (
          <SwiperStyled
            slidesPerView={1}
            spaceBetween={0}
            autoplay={{ delay: 3500, disableOnInteraction: false }}
            loop={true}
            pagination={{
              clickable: true,
            }}
            modules={[Pagination, Navigation, FreeMode, Autoplay, EffectFade]}
            effect="fade"
            centeredSlides={true}
            freeMode={true}
          >
            {dish.images.map((image) => (
              <SwiperSlide key={image}>
                <CardMedia sx={{ height: 500, width: "100%" }} image={getThumbnailImage({ file: image, organizationId, size: "800x500" })} component="img" alt={dish?.title} />
              </SwiperSlide>
            ))}
          </SwiperStyled>
        ) : (
          <CardMedia sx={{ height: 500, width: "100%" }} image={IMAGE_PLACEHOLDER} component="img" alt={dish?.title} />
        )}
        <Stack
          p={3}
          spacing={2}
          sx={{
            mt: "-30px",
            zIndex: "30",
            backgroundColor: "white !important",
            position: "relative",
            borderTopLeftRadius: "24px",
            borderTopRightRadius: "24px",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h2" fontWeight={600} flex={1} sx={{ textDecoration: isDisabled ? "line-through" : "none" }}>
              {dish.title}
            </Typography>
            {price >= 0 ? (
              <Typography variant="h3" fontSize={"24px"} component="p">
                <Price currency="NIO">{price}</Price>
              </Typography>
            ) : null}
          </Stack>

          {dish.schedule ? <ScheduleAvailability availability={dish?.schedule} /> : null}

          {dish?.description ? parse(dish?.description) : null}

          {/* {settings?.length ? (
            <>
              <Divider>Configuración</Divider>
              <ProductSettings settings={settings} />
            </>
          ) : null} */}
          {settings.map((s) => s.isAvailable) && settings.map((s) => s.isAvailable).some((isAvailable) => isAvailable) ? (
            <>
              <Divider>Configuración</Divider>
              <ProductSettings settings={settings} />
            </>
          ) : null}

          <RelatedProducts dish={dish} />

          <Divider />
          <TextField multiline rows={3} label="Instrucciones" name="comments" value={values.comments} onChange={handleChange} />
        </Stack>
      </Scrollbars>
      <Stack
        direction={"row"}
        spacing={2}
        justifyContent="center"
        sx={{
          background: "white",
          padding: 2,
          position: "relative",
          zIndex: "30",
          boxShadow: "0px -2px 8px #0000001f",
        }}
      >
        <QuantitySelector total={values.quantity} onAdd={increaseQuantity} onRemove={reduceQuantity} minimum={1} />
        <Divider orientation="vertical" />

        <Button
          color="primary"
          disabled={isLoading || isDisabled || !isDishAvailableToday}
          onClick={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          sx={{ paddingLeft: 4, paddingRight: 4 }}
        >
          <Price currency="NIO" preffix="Añadir ">
            {multiplyByQuantity(values?.quantity, price)}
          </Price>
        </Button>
      </Stack>
    </Stack>
  );
};

export default RestaurantForm;
